import { useEffect, useState } from "react";
import { Breadcrumbs, DateTimeDisplay, StatusMessage } from "../../components/ui";
import BulkActionToolbar from "../../components/ui/BullActionToolbar";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { PendingSiteList } from "../../features/pending/PendingSiteList";
import { PendingSitesEditor } from "../../features/pending/PendingSitesEditor";
import { usePendingSites } from "../../features/pending/usePendingSites";
import { getPendingKey } from "../../features/pending/pendingSites";
import type { PendingAction, PendingMode, PendingSite, PendingWorkflow } from "../../features/pending/pendingSites";

const Pending = () => {
    const data = usePendingSites();
    const breadcrumbs = useBreadcrumbs({ currentPageTitle: "Pending Sites" });
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [workflow, setWorkflow] = useState<PendingWorkflow | null>(null);
    const [scrolledDown, setScrolledDown] = useState(false);
    useEffect(() => {
        const onScroll = () => setScrolledDown(window.scrollY > 120);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    useEffect(() => {
        setSelectedIds(previous => new Set([...previous].filter(id => data.sites.some(site => site.id === id))));
    }, [data.sites]);
    const open = (sites: PendingSite[], mode: PendingMode, bulk: boolean) => {
        if (!sites.length || data.submitting) {return;}
        data.setError(null);
        data.setOperationError(null);
        setWorkflow({ sites, mode, bulk });
    };
    const cancel = () => { if (!data.submitting) { setWorkflow(null); setSelectedIds(new Set()); } };
    const submit = async (items: PendingAction[]) => {
        if (!workflow || data.submitting) {return;}
        const completed = new Set(await data.process(items, workflow.mode));
        setSelectedIds(previous => new Set([...previous].filter(id => !completed.has(id))));
        setWorkflow(previous => {
            if (!previous) {return null;}
            const remaining = previous.sites.filter(site => !completed.has(site.id));
            return remaining.length ? { ...previous, sites: remaining } : null;
        });
    };
    const remove = (key: string) => {
        if (!workflow || data.submitting) {return;}
        const removed = workflow.sites.find(site => getPendingKey(site) === key);
        if (removed) setSelectedIds(previous => new Set([...previous].filter(id => id !== removed.id)));
        const sites = workflow.sites.filter(site => getPendingKey(site) !== key);
        setWorkflow(sites.length ? { ...workflow, sites } : null);
    };
    const selected = data.sites.filter(site => selectedIds.has(site.id));
    return (
        <main>
            <BulkActionToolbar visible={!!selected.length && !workflow} scrolled={scrolledDown && !selected.length && !workflow}>
                <button type="button" className="btn btn__toolbar--approve" disabled={!selected.length || data.submitting}
                    onClick={() => open(selected, "approve", true)}>Approve Selected</button>
                <button type="button" className="btn btn__toolbar--reject" disabled={!selected.length || data.submitting}
                    onClick={() => open(selected, "reject", true)}>Reject Selected</button>
                <button type="button" className="btn btn__toolbar--cancel" disabled={!selected.length || data.submitting} onClick={cancel}>Cancel</button>
            </BulkActionToolbar>
            <div className="container mb-2">
                <section className="page">
                    <h2 className="heading__title heading__title--pending-sites">Pending Sites</h2>
                    <DateTimeDisplay />
                </section>
                <Breadcrumbs items={breadcrumbs} />
                {data.success && <StatusMessage kind="success" onClose={() => data.setSuccess(null)}>{data.success.message}</StatusMessage>}
                {data.error && <StatusMessage kind="error" onClose={() => data.setError(null)}>{data.error}</StatusMessage>}
                <section className="page"><div className="alarm-content">
                    <PendingSiteList sites={data.sites} loading={data.loading} busy={data.submitting || !!workflow}
                        selectedIds={selectedIds} onSelectionChange={setSelectedIds} onAction={(site, mode) => open([site], mode, false)} />
                </div></section>
                {workflow && <PendingSitesEditor workflow={workflow} submitting={data.submitting} error={data.operationError}
                    onSubmit={submit} onCancel={cancel} onRemove={remove} />}
            </div>
        </main>
    );
};

export default Pending;
